import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { MELDUNG_ERWEITERUNG_FEHLT, istFehlendeErweiterung } from './sifaService';
import { SifaRpcFehler, fehlerCode, rpcAntwortObjekt, rpcFeld } from './sifaPublicService';
import type { DatenschutzFassung, DatenschutzText } from './types';

/**
 * Datenschutzhinweise der Online-Unterweisung je Firma — Tabelle
 * `sifa_datenschutz_texte` (Nr. 143) und die anonymen RPCs aus Nr. 144.
 * Vertrag: docs/sifa-ausbau2-plan.md §3.2.
 *
 * NUR EINFUEGEN, NIE AENDERN: jede gespeicherte Fassung bleibt, denn jede
 * Teilnahme verweist auf die Fassung, die der Teilnehmer bestaetigt hat.
 * Speichern heisst deshalb immer „neue Fassung = hoechste + 1"; der harte
 * Riegel ist unique (company_id, fassung) — bei 23505 wird neu gezaehlt.
 *
 * FAIL-OPEN: ohne Nr. 143/144 liefern die Lesewege null bzw. [] (der
 * Aufrufer zeigt das eingebaute Muster), Speichern wirft Klartext.
 */

const T_DATENSCHUTZ = 'sifa_datenschutz_texte';

/** Wie oft bei Fassungs-Kollision (zwei Admins gleichzeitig) erneut gezaehlt wird. */
const MAX_VERSUCHE = 4;

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

const werfe = (e: unknown): never => {
  if (istFehlendeErweiterung(e)) throw new Error(MELDUNG_ERWEITERUNG_FEHLT);
  if (errorCode(e) === '42501') throw new Error(`Datenschutzhinweise pflegen nur Admins. (${describeError(e)})`);
  throw new Error(describeError(e));
};

export const mapDatenschutzFassung = (row: Zeile): DatenschutzFassung => ({
  id: str(row.id),
  fassung: num(row.fassung, 0),
  titel: str(row.titel),
  text: str(row.text),
  erstelltVonName: strOrNull(row.erstellt_von_name),
  createdAt: str(row.created_at),
});

/** Firma und Anzeigename des angemeldeten Profils — best effort. */
const eigenesProfil = async (): Promise<{ companyId: string | null; name: string | null }> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user?.id) return { companyId: null, name: null };
    const { data } = await supabase
      .from('profiles')
      .select('first_name, last_name, company_id')
      .eq('id', user.id)
      .maybeSingle();
    const p = (data ?? null) as { first_name?: string; last_name?: string; company_id?: unknown } | null;
    const name = ([p?.first_name, p?.last_name].filter(Boolean).join(' ') || user.email || '').trim();
    const companyId = str(p?.company_id).trim();
    return { companyId: companyId || null, name: name || null };
  } catch {
    return { companyId: null, name: null };
  }
};

/**
 * Die geltende (hoechste) Fassung der EIGENEN Firma. Ausdruecklich mit
 * `eq('company_id')`: ein Admin sieht per RLS alle Firmen, und „die neueste
 * irgendeiner Firma" waere hier falsch. Ohne Firma im Profil null.
 * Fehlt Nr. 143: null (Aufrufer zeigt das Muster); andere Fehler werfen.
 */
export const fetchDatenschutzAktuell = async (): Promise<DatenschutzText | null> => {
  const { companyId } = await eigenesProfil();
  if (!companyId) return null;
  try {
    const { data, error } = await supabase
      .from(T_DATENSCHUTZ)
      .select('*')
      .eq('company_id', companyId)
      .order('fassung', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? mapDatenschutzFassung(data as Zeile) : null;
  } catch (e) {
    if (istFehlendeErweiterung(e)) return null;
    return werfe(e);
  }
};

/** Alle Fassungen der eigenen Firma, neueste zuerst — die Versionsliste der Pflege. Fehlt Nr. 143: []. */
export const fetchDatenschutzFassungen = async (): Promise<DatenschutzFassung[]> => {
  const { companyId } = await eigenesProfil();
  if (!companyId) return [];
  try {
    const { data, error } = await supabase
      .from(T_DATENSCHUTZ)
      .select('*')
      .eq('company_id', companyId)
      .order('fassung', { ascending: false });
    if (error) throw error;
    return (Array.isArray(data) ? data : []).map((r: Zeile) => mapDatenschutzFassung(r));
  } catch (e) {
    if (istFehlendeErweiterung(e)) return [];
    return werfe(e);
  }
};

/** Hoechste Fassung der Firma, frisch aus der Datenbank (0 = noch keine). */
const hoechsteFassung = async (companyId: string): Promise<number> => {
  const { data, error } = await supabase
    .from(T_DATENSCHUTZ)
    .select('fassung')
    .eq('company_id', companyId)
    .order('fassung', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const n = num((data as { fassung?: unknown } | null)?.fassung, 0);
  return n > 0 ? n : 0;
};

/**
 * Speichert eine NEUE Fassung (hoechste + 1) der eigenen Firma — nie ein
 * Update. Die Fassung wird in der Schleife unmittelbar vor dem Insert
 * gezaehlt; bei 23505 (ein zweiter Admin war schneller) neu zaehlen und
 * erneut versuchen. Platzhalter `{{…}}` bleiben im gespeicherten Text, die
 * Teilnehmerseite fuellt sie (fuelleDatenschutz).
 */
export const speichereDatenschutz = async (titel: string, text: string): Promise<DatenschutzText> => {
  const t = (titel ?? '').trim();
  if (!t) throw new Error('Bitte einen Titel für die Datenschutzhinweise eingeben.');
  if (!(text ?? '').trim()) throw new Error('Der Text der Datenschutzhinweise darf nicht leer sein.');
  const { companyId, name } = await eigenesProfil();
  if (!companyId) throw new Error('Ohne Firma im eigenen Profil lassen sich keine Datenschutzhinweise speichern.');

  for (let versuch = 1; ; versuch++) {
    try {
      const fassung = (await hoechsteFassung(companyId)) + 1;
      const zeile: Zeile = { company_id: companyId, fassung, titel: t, text };
      if (name) zeile.erstellt_von_name = name;
      const { data, error } = await supabase.from(T_DATENSCHUTZ).insert(zeile).select('*').single();
      if (error) throw error;
      return mapDatenschutzFassung(data as Zeile);
    } catch (e) {
      if (errorCode(e) === '23505' && versuch < MAX_VERSUCHE) continue;
      return werfe(e);
    }
  }
};

/** Datenschutzhinweise fuer die anonyme Seite: Text der Firma plus die Namen fuer fuelleDatenschutz. */
export type DatenschutzPublic = DatenschutzText & { kundeName: string; bueroName: string };

/** Je Einstieg eine RPC (Nr. 144) — Einzel-Link, Sammel-Link, Test-Link. */
const PUBLIC_RPC: Record<'link' | 'sammel' | 'test', { name: string; parameter: string }> = {
  link: { name: 'sifa_datenschutz_public', parameter: 'p_link_token' },
  sammel: { name: 'sifa_datenschutz_sammel_public', parameter: 'p_token' },
  test: { name: 'sifa_datenschutz_test_public', parameter: 'p_token' },
};

/**
 * Die geltenden Datenschutzhinweise zum Link — die Firma des Projektbesitzers
 * bestimmt die Datenbank. null = keine eigene Fassung, unbekannter Token oder
 * Nr. 144 fehlt: der Aufrufer zeigt dann das Muster. Ein leerer Text gilt als
 * keiner. Andere Fehler werfen (SifaRpcFehler mit SQLSTATE).
 */
export const ladeDatenschutzPublic = async (
  quelle: { art: 'link' | 'sammel' | 'test'; token: string },
): Promise<DatenschutzPublic | null> => {
  const rpc = PUBLIC_RPC[quelle?.art];
  const token = (quelle?.token || '').trim();
  if (!rpc || !token) return null;
  try {
    const { data, error } = await supabase.rpc(rpc.name, { [rpc.parameter]: token });
    if (error) throw error;
    const row = rpcAntwortObjekt(data);
    if (!row || !str(row.text).trim()) return null;
    const id = str(rpcFeld(row, 'id')).trim();
    return {
      id: id || null,
      fassung: num(rpcFeld(row, 'fassung'), 0),
      titel: str(row.titel),
      text: str(row.text),
      kundeName: str(rpcFeld(row, 'kundeName', 'kunde_name')),
      bueroName: str(rpcFeld(row, 'bueroName', 'buero_name')),
    };
  } catch (e) {
    if (istFehlendeErweiterung(e)) return null;
    throw new SifaRpcFehler(describeError(e), fehlerCode(e));
  }
};
